// src/Components/modules/patients/Form/ResponsablesList.js

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Form, Button, Typography, Alert, Switch, Space } from "antd";
import { PlusOutlined, TeamOutlined } from "@ant-design/icons";
import ResponsableItem from "./ResponsableItem";

const { Text } = Typography;

const MAX_RESPONSABLES = 5;

/**
 * Componente para la lista dinámica de responsables del paciente.
 * Permite hasta 5 responsables, siendo el primero el principal.
 * Incluye opción para marcar paciente sin responsables.
 */
const ResponsablesList = ({ form, tiposDocumento }) => {
  // Observar el estado del switch
  const sinResponsables = Form.useWatch("sin_responsables", form);

  // Limpiar responsables cuando se activa el switch
  useEffect(() => {
    if (sinResponsables) {
      form.setFieldsValue({ responsables: [] });
    } else {
      // Si se desactiva y no hay responsables, agregar uno vacío
      const currentResponsables = form.getFieldValue("responsables");
      if (!currentResponsables || currentResponsables.length === 0) {
        form.setFieldsValue({
          responsables: [
            {
              nombre: "",
              tipo_documento_id: null,
              numero_documento: "",
              parentesco: null,
              telefono: "",
            },
          ],
        });
      }
    }
  }, [sinResponsables, form]);

  return (
    <div>
      {/* Alerta informativa con switch */}
      <Alert
        message={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <span>Responsables del Paciente</span>
            <Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Sin responsables
              </Text>
              <Form.Item
                name="sin_responsables"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch size="small" />
              </Form.Item>
            </Space>
          </Space>
        }
        description={
          !sinResponsables && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Puede agregar hasta {MAX_RESPONSABLES} responsables. El primero
              será considerado el responsable principal.
            </Text>
          )
        }
        type="info"
        showIcon
        icon={<TeamOutlined />}
        style={{ marginBottom: 16 }}
      />

      {/* Mostrar lista de responsables solo si NO está marcado sin responsables */}
      {!sinResponsables && (
        <Form.List
          name="responsables"
          rules={[
            {
              validator: async (_, responsables) => {
                const sinResp = form.getFieldValue("sin_responsables");
                if (sinResp) {
                  return Promise.resolve();
                }
                if (!responsables || responsables.length < 1) {
                  return Promise.reject(
                    new Error("Debe agregar al menos un responsable")
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => (
            <div>
              {/* Lista de responsables */}
              {fields.map((field, index) => (
                <ResponsableItem
                  key={field.key}
                  index={index}
                  field={field}
                  remove={remove}
                  isFirst={index === 0}
                  canDelete={fields.length > 1}
                  tiposDocumento={tiposDocumento}
                />
              ))}

              {/* Errores de validación */}
              <Form.ErrorList errors={errors} />

              {/* Botón para agregar responsable */}
              {fields.length < MAX_RESPONSABLES && (
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      nombre: "",
                      tipo_documento_id: null,
                      numero_documento: "",
                      parentesco: null,
                      telefono: "",
                    })
                  }
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                >
                  Agregar Responsable ({fields.length}/{MAX_RESPONSABLES})
                </Button>
              )}

              {/* Mensaje cuando se alcanza el límite */}
              {fields.length >= MAX_RESPONSABLES && (
                <Text
                  type="secondary"
                  style={{ display: "block", textAlign: "center", marginTop: 8 }}
                >
                  Límite de {MAX_RESPONSABLES} responsables alcanzado
                </Text>
              )}
            </div>
          )}
        </Form.List>
      )}

      {/* Mensaje cuando está marcado sin responsables */}
      {sinResponsables && (
        <Alert
          message="Este paciente no tiene responsables asignados"
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
        />
      )}
    </div>
  );
};

ResponsablesList.propTypes = {
  form: PropTypes.object.isRequired,
  tiposDocumento: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      descripcion: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default ResponsablesList;
